// src/hooks/useFeed.js
// All community feed and SOS state in one hook.
import { ZONE_CENTERS } from '@data/zoneCenters';
// FeedPage becomes a pure render component — no logic.
//
// Returns:
//   Feed data:   posts, filtered, activeFilter, setFilter
//   SOS flow:    sos.* — show, selected, note, address, broadcast(), reset()
//   Compose:     compose.* — show, draft, type, submit(), reset()
//   Submission:  submitting, submitError

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useFeedStore, useUserStore } from '@store';
import { writeSOS, writeCommunityPost } from '@services/sync/writers.js';
import { buildSOSFallbackSMS } from '@services/notifications/sms';
import { FeedWorker } from '@workers';
import { useGeolocation } from './index';

export function useFeed() {
  const zone    = useUserStore((s) => s.zone);     // home zone (for submissions)
  const zones   = useUserStore((s) => s.zones);    // all watched zones (for feed)
  const posts   = useFeedStore((s) => s.posts);
  const geo     = useGeolocation();

  // Memoize activeZones so the array reference is stable across renders.
  // Without useMemo, a new [] is created every render, causing the filter
  // effect to re-run even when the zones haven't changed.
  const activeZones = useMemo(
    () => zones.length > 0 ? zones : (zone ? [zone] : []),
    [zones, zone]
  );

  // ── Filter ──────────────────────────────────────────────────────
  const [activeFilter, setFilter] = useState('all');
  const [showAllZones, setShowAllZones] = useState(false);

  // Feed processing runs in a Web Worker — never blocks the render thread.
  const [filtered, setFiltered] = useState([]);
  const prevFilteredRef = useRef([]);

  // Stable-reference wrapper: only update filtered if content actually changed.
  // Prevents FeedPage from re-rendering when the worker returns identical data.
  const setFilteredStable = useCallback((next) => {
    const prev = prevFilteredRef.current;
    // Fast path: same length and same IDs+confidenceMid in same order = skip update
    const changed = next.length !== prev.length ||
      next.some((p, i) => p.id !== prev[i]?.id ||
                           p.confidenceMid !== prev[i]?.confidenceMid ||
                           p.sev !== prev[i]?.sev ||
                           p.isPinned !== prev[i]?.isPinned);
    if (changed) {
      prevFilteredRef.current = next;
      setFiltered(next);
    }
  }, []);
  const [feedCounts, setFeedCounts] = useState({});
  const filterRef = useRef(activeFilter);
  filterRef.current = activeFilter;

  useEffect(() => {
    let cancelled = false;
    // Pre-filter by active zones before passing to worker
    const MAX_ALL_ZONES = 500;   // cap "show all" to avoid overwhelming the worker
    const zonePosts = showAllZones
      ? posts
          .filter(p => p.lifeSafetyPriority != null ? p.lifeSafetyPriority > 0 : true)
          .sort((a, b) => (b.lifeSafetyPriority ?? 0) - (a.lifeSafetyPriority ?? 0))
          .slice(0, MAX_ALL_ZONES)
      : posts.filter(p =>
          !p.zone ||                           // no zone tag = show everywhere
          activeZones.includes(p.zone) ||      // in user's watched zones
          p.type === 'sos'                     // SOS always visible regardless of zone
        );
    FeedWorker.sortAndFilter(zonePosts, activeFilter).then(({ posts: result, counts }) => {
      if (!cancelled) {
        setFilteredStable(result);
        setFeedCounts(counts);
      }
    }).catch(() => {
      // Worker unavailable — fall back to synchronous filter
      if (!cancelled) {
        setFilteredStable(posts.filter(p => !p.isHidden).sort((a, b) => {
          // Life safety > verification confidence > freshness > proximity
          const lsA = a.lifeSafetyPriority ?? 0;
          const lsB = b.lifeSafetyPriority ?? 0;
          if (lsB !== lsA) return lsB - lsA;
          const confA = a.confidenceMid ?? 0;
          const confB = b.confidenceMid ?? 0;
          if (Math.abs(confB - confA) > 0.15) return confB - confA;
          return (b.ts ?? 0) - (a.ts ?? 0);
        }));
      }
    });
    return () => { cancelled = true; };
  }, [posts, activeFilter, showAllZones, activeZones]);

  // ── SOS flow ────────────────────────────────────────────────────
  const [showSOS,     setShowSOS]     = useState(false);
  const [sosSelected, setSosSelected] = useState(null);   // { id, label, emoji, body }
  const [sosNote,     setSosNote]     = useState('');
  const [sosAddress,  setSosAddress]  = useState('');
  const [sosUnit,     setSosUnit]     = useState('');
  const [sosManual,   setSosManual]   = useState(false);
  const [sosSubmitting, setSosSubmitting] = useState(false);
  const [sosError,    setSosError]    = useState(null);
  const [smsFallback, setSmsFallback] = useState(null);

  const resetSOS = useCallback(() => {
    setShowSOS(false);
    setSosSelected(null);
    setSosNote('');
    setSosAddress('');
    setSosUnit('');
    setSosManual(false);
    setSosError(null);
  }, []);

  const broadcastSOS = useCallback(async ({ mediaUrls = [] } = {}) => {
    const cleanNote = (sosNote ?? '').trim().slice(0, 200); // cap at 200 chars
    const parts = [sosSelected?.body ?? 'I need emergency assistance.'];
    if (sosNote) parts.push(sosNote);
    const address = sosAddress
      ? (sosUnit ? `${sosAddress} ${sosUnit}` : sosAddress)
      : null;

    setSosSubmitting(true);
    setSosError(null);
    try {
      await writeSOS({
        sosSubtype:        sosSelected?.id ?? 'other',
        sosUrgency:        sosSelected?.urgency ?? 'urgent',
        body:              [...parts, cleanNote].filter(Boolean).join(' | '),
        lat:               geo.lat ?? ZONE_CENTERS[zone]?.lat ?? null,
        lon:               geo.lon ?? ZONE_CENTERS[zone]?.lon ?? null,
        locationAccuracyM: geo.lat ? (geo.accuracy ?? null) : null,
        address,
        zone:      zone ?? 'unknown',
        mediaUrls,
      });
      resetSOS();
      // Trigger intelligence scoring in background (non-blocking)
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const { getApp } = await import('firebase/app');
        const fn = httpsCallable(getFunctions(getApp()), 'scoreReport');
        // fn({ reportId }) — called after sync confirms ID
      } catch {}
    } catch (err) {
      setSosError(err.message);
      // Build SMS fallback so user can text SOS even without data
      const fallback = buildSOSFallbackSMS({
        sosSubtype: sosSelected?.id ?? 'other',
        body:       [sosSelected?.body, cleanNote].filter(Boolean).join(' | '),
        address:    sosAddress ? (sosUnit ? `${sosAddress} ${sosUnit}` : sosAddress) : null,
        zone,
      });
      setSmsFallback(fallback);
    } finally {
      setSosSubmitting(false);
    }
  }, [sosSelected, sosNote, sosAddress, sosUnit, geo, zone, resetSOS]);

  // ── Compose ──────────────────────────────────────────────────────
  const [showCompose, setShowCompose] = useState(false);
  const [draft,       setDraft]       = useState('');
  const [postType,    setPostType]    = useState('info');
  const [composeSubmitting, setComposeSubmitting] = useState(false);
  const [composeError,      setComposeError]      = useState(null);

  const resetCompose = useCallback(() => {
    setShowCompose(false);
    setDraft('');
    setPostType('info');
    setComposeError(null);
  }, []);

  const submitPost = useCallback(async () => {
    if (!draft.trim()) return;
    setComposeSubmitting(true);
    setComposeError(null);
    try {
      await writeCommunityPost({ body: draft.trim(), type: postType, zone: zone ?? 'unknown' });
      resetCompose();
    } catch (err) {
      setComposeError(err.message);
    } finally {
      setComposeSubmitting(false);
    }
  }, [draft, postType, zone, resetCompose]);

  return {
    // Feed data
    posts,
    filtered,
    feedCounts,
    activeFilter,
    setFilter,
    zone,
    zones:          activeZones,
    showAllZones,
    setShowAllZones,

    // SOS
    sos: {
      show:        showSOS,
      setShow:     setShowSOS,
      selected:    sosSelected,
      setSelected: setSosSelected,
      note:        sosNote,
      setNote:     setSosNote,
      address:     sosAddress,
      setAddress:  setSosAddress,
      unit:        sosUnit,
      setUnit:     setSosUnit,
      manual:      sosManual,
      setManual:   setSosManual,
      submitting:  sosSubmitting,
      error:       sosError,
      smsFallback,
      clearSmsFallback: () => setSmsFallback(null),
      broadcast:   broadcastSOS,
      reset:       resetSOS,
    },

    // Compose
    compose: {
      show:       showCompose,
      setShow:    setShowCompose,
      draft,
      setDraft,
      postType,
      setPostType,
      submitting: composeSubmitting,
      error:      composeError,
      submit:     submitPost,
      reset:      resetCompose,
    },

    // GPS (needed by SOS flow)
    geo,
  };
}
